import * as React from 'react';
import {
  Field,
  FieldType,
  OptionsControlType,
  OptionsField,
  Primitive,
  ReferenceField,
  RichtextField,
  Row,
  getFieldStringFromPrimitive,
  getTooltipHoverText,
} from 'gossamer-unified';
import { Boolean } from './Boolean';
import { ChecksGroup } from './ChecksGroup';
import { Dropdown } from './Dropdown';
import { Editable } from './Editable';
import { File } from './File';
import { Flex } from './Flex';
import { Reference } from './Reference';
import { Richtext } from './Richtext';
import { Uneditable } from './Uneditable';
import { ColourPicker } from './ColourPicker';

export interface GeneralProps {
  className?: string;
  clearOnSelection?: boolean;
  editable: boolean;
  excludeValues?: string[];
  fieldId: string;
  field: Field;
  handleBlur?: (newValue: string) => void;
  handleChange?: (newValue: string | null) => void;
  placeholder?: string;
  suppressLink?: boolean;
  validity?: boolean;
  value: Primitive;
  row?: Row;
}

export const General: React.FC<GeneralProps> = (props) => {
  const mandatory = !!props.field.mandatory;
  const handleChange = props.handleChange || (() => undefined);
  const stringValue = getFieldStringFromPrimitive(props.field.type, props.value);
  let output;
  if (props.field.renderEditable && props.editable) {
    output = props.field.renderEditable(props);
  } else if (props.field.renderUneditable && !props.editable) {
    output = props.field.renderUneditable(props);
  } else if (props.field.type === FieldType.Boolean) {
    output = (
      <Boolean
        editable={props.editable}
        field={props.field}
        handleChange={props.handleChange}
        validity={props.validity}
        value={stringValue}
      />
    );
  } else if (props.field.type === FieldType.Colour) {
    output = (
      <ColourPicker
        editable={props.editable}
        handleChange={props.handleChange}
        mandatory={mandatory}
        validity={props.validity}
        value={stringValue}
      />
    );
  } else if (props.field.type === FieldType.Options && props.editable) {
    if (props.field.excludeValues) {
      (props.field as OptionsField).options.forEach((option) => {
        option.active = props.field.excludeValues?.indexOf(option.id) === -1;
      });
    }
    if (
      !!props.field.controlType &&
      [OptionsControlType.RadioHorizontal, OptionsControlType.RadioVertical].indexOf(props.field.controlType) > -1
    ) {
      output = (
        <ChecksGroup
          className={props.className}
          clearOnSelection={props.clearOnSelection}
          excludeValues={props.excludeValues}
          inline={props.field.controlType === OptionsControlType.RadioHorizontal}
          options={props.field.options}
          onItemSelected={handleChange}
          mandatory={mandatory}
          type="radio"
          validity={props.validity}
          value={stringValue}
        />
      );
    } else {
      output = (
        <Dropdown
          className={props.className}
          clearOnSelection={props.clearOnSelection}
          excludeValues={props.excludeValues}
          options={props.field.options}
          onItemSelected={handleChange}
          mandatory={mandatory}
          validity={props.validity}
          value={stringValue}
        />
      );
    }
  } else if (props.field.type === FieldType.File) {
    output = (
      <File
        allowedExtensions={props.field.allowedExtensions}
        editable={props.editable}
        handleChange={handleChange}
        mandatory={mandatory}
        maxSize={props.field.maxSize}
        imagesOnly={!!props.field.imagesOnly}
        storageType={props.field.storageType!}
        storeMimeTypeAndTitle={!!props.field.storeMimeTypeAndTitle}
        validity={props.validity}
        value={stringValue}
      />
    );
  } else if (props.field.type === FieldType.Flex) {
    output = <Flex fieldId={props.fieldId} value={stringValue} />;
  } else if (props.field.type === FieldType.Reference) {
    output = (
      <Reference
        className={props.className}
        clearOnSelection={props.clearOnSelection}
        editable={props.editable}
        field={props.field as ReferenceField}
        onItemSelected={handleChange}
        suppressLink={props.suppressLink}
        validity={props.validity}
        value={stringValue}
      />
    );
  } else if (props.field.type === FieldType.Richtext && props.editable) {
    output = (
      <Richtext
        className={props.className}
        editorHeight={(props.field as RichtextField).editorHeight}
        handleChange={handleChange}
        mandatory={mandatory}
        validity={props.validity}
        value={stringValue}
      />
    );
  } else if (props.editable) {
    output = (
      <Editable
        className={props.className}
        clearOnSelection={props.clearOnSelection}
        field={props.field}
        handleBlur={props.handleBlur}
        handleChange={props.handleChange}
        placeholder={props.placeholder}
        validity={props.validity}
        value={stringValue}
      />
    );
  } else {
    output = <Uneditable className={props.className} field={props.field} value={stringValue} />;
  }

  if (props.field.displayUrl && props.row) {
    const displayUrl = props.field.displayUrl(props.row);
    if (displayUrl) {
      output = <a href={displayUrl}>{output}</a>;
    }
  }
  if (props.field.tooltipText) {
    const tooltipText = getTooltipHoverText(props.field, props.row);
    return (
      <div style={{ display: 'flex' }}>
        {output}
        {tooltipText && (
          <>
            &nbsp;&nbsp;
            <a style={{ color: 'black' }} title={tooltipText}>
              ⓘ
            </a>
          </>
        )}
      </div>
    );
  }
  return output;
};
